import React, { useState, useCallback, FC } from "react";
import cn from "clsx";
import { gql } from "@apollo/client";

import MobileActionsBar from "./MobileActionsBar";
import ToggleSwitch from "./ToggleSwitch";
import PremiumFeatureList, {
  PremiumFeatureListFragments
} from "./PremiumFeatureList";

import CoinCalculatorModal from "../Modals/CoinCalculatorModal";

import { isCompositionPremium } from "../../utils/compositions";

import usePageDefaults from "../../hooks/usePageDefaults";
import useMobile from "../../hooks/useMobile";

import {
  TitleAndFavorite,
  PartnerLink,
  CustomizeButton,
  DescriptionParagraph,
  FlyerFeatures,
} from "./PDPDetailsComponents";

interface Props {
  className?: string;
  customizeURL: string;
  name: string;
  partner: any;
  eventType: string;
  comps: any;
  premiumLineItems: any[];
  isVariant: boolean;
  coinsPerRecipient: number;
  shareImage: string;
  toggleValue: number;
  toggleCallback: any;
}

const FREE_PRIMARY_OPTIONS = [ 'Free', 'Premium' ];
const PREMIUM_PRIMARY_OPTIONS = [ 'Premium', 'Free' ];

const FlyerCompositionDetails: FC<Props> = (props) => {
  const isMobile = useMobile();
  const { pageDefaults: { freeSendCap } } = usePageDefaults();
  const [ isCoinCalculatorModalOpen, setIsCoinCalculatorModalOpen] = useState(false);
  const setCoinCalculatorModalOpen = useCallback(
    () => setIsCoinCalculatorModalOpen(true), [setIsCoinCalculatorModalOpen]
  );
  const setCoinCalculatorModalDismiss = useCallback(
    () => setIsCoinCalculatorModalOpen(false), [setIsCoinCalculatorModalOpen]
  );
  const {
    className,
    name,
    eventType,
    partner,
    comps,
    premiumLineItems,
    coinsPerRecipient,
    customizeURL,
    isVariant,
    shareImage,
    toggleValue,
    toggleCallback,
  } = props;

  const isPremium = premiumLineItems.length > 0;
  const customizeText = isPremium ? "Customize" : "Customize for free";

  const PREMIUM_COINS_TITLE = `${coinsPerRecipient} Coins per invite`;
  const FREE_COINS_TITLE = `Your first ${freeSendCap} invites are free`;
  const PREMIUM_FLYER_DESCRIPTION = `Premium designs include upgraded page styles and Premium colors. Premium options require Coins for sending. Use our calculator below to see how many Coins you’ll need.`;
  const FREE_FLYER_DESCRIPTION = `Your first ${freeSendCap} trackable email or text message invites are free, or share a link to unlimited guests without tracking. Each trackable invite after ${freeSendCap} is one Coin per invite. Upgrade to Premium for more features and color choices.`;

  const CUSTOMIZE_TRACKING_PROPS = (
    {
      id: customizeText, 
      "ispremium": isPremium,
      "variant_type": isVariant
        ? 'variant'
        : comps?.length > 1 ? 'primary' : 'none',
    }
  );

  return (
    <div
      className={
        cn(
          className, 
          'bg-white text-lapel relative px-4 lg:px-6 lg:pt-16 lg:shadow-[-1px_0_10px_0px_rgba(0,0,0,0.25)]'
        )
      }
    >
      <div className="mb-6">
        <TitleAndFavorite name={name} />
        <PartnerLink partner={partner} />
      </div>
      {comps.length > 1 && isMobile === false && (
        <div className="flex items-center my-6">
          <ToggleSwitch
            options={isCompositionPremium(comps[0]) ? PREMIUM_PRIMARY_OPTIONS : FREE_PRIMARY_OPTIONS}
            toggleValue={toggleValue}
            toggleCallback={toggleCallback}
          />
          <span
            className="text-gatsby cursor-pointer paragraph-medium ml-4"
            onClick={setCoinCalculatorModalOpen}
          >
            Pricing
          </span>
        </div>
      )}
      <p className="font-medium paragraph-small text-lapel my-1">
        {isPremium ? PREMIUM_COINS_TITLE : FREE_COINS_TITLE}
      </p>
      <DescriptionParagraph
        description={isPremium ? PREMIUM_FLYER_DESCRIPTION : FREE_FLYER_DESCRIPTION}
      />
      {!isMobile && comps.length === 1 && (
        <span
          className="text-gatsby cursor-pointer paragraph-small"
          onClick={setCoinCalculatorModalOpen}
        >
          Pricing
        </span>
      )}
      {isMobile === false && (
        <CustomizeButton
          className={cn({ 'mt-6 mb-2': comps.length > 1, 'my-6': comps.length === 1 })}
          href={customizeURL}
          tracking={CUSTOMIZE_TRACKING_PROPS}
        >
          { customizeText }
        </CustomizeButton>
      )}
      {!isPremium && comps.length > 1 && (
        <button
          className="w-full h-12 px-6 py-4 mb-2 text-gatsby paragraph-small leading-none"
          onClick={() => toggleCallback(
            !isCompositionPremium(comps[0]) ? 1 : 0,
            "See Premium Version"
          )}
        >
          See Premium Version
        </button>
      )}
      {premiumLineItems?.length > 0 && (
        <PremiumFeatureList
          premiumLineItems={premiumLineItems}
          coinsPerRecipient={coinsPerRecipient}
          openCoinInfoModalCallback={setCoinCalculatorModalOpen}
        />
      )}
      <FlyerFeatures />
      {isMobile && (
        <MobileActionsBar
          name={name}
          comps={comps}
          partner={partner}
          eventType={eventType}
          customizeURL={customizeURL}
          toggleCallback={toggleCallback}
          toggleValue={toggleValue}
          productType="flyer"
          customizeTrackingProps={CUSTOMIZE_TRACKING_PROPS}
          shareImage={shareImage}
          setCoinModalOpen={setCoinCalculatorModalOpen}
        />
      )}
      {isCoinCalculatorModalOpen && (
        <CoinCalculatorModal
          coinsPerRecipient={coinsPerRecipient}
          isFreeInvite={!isPremium}
          isInvite={true}
          isOpen={isCoinCalculatorModalOpen}
          onDismiss={setCoinCalculatorModalDismiss}
        />
      )}
    </div>
  );
};

const FlyerCompositionDetailsFragments = gql`
  fragment FlyerCompositionDetailsFragment on FlyerCompositionDetail {
    id
    name
    coinsPerRecipient
    slug
    eventType
    designer {
      name
      meta
    }
    isVariant
    ...PremiumFeatureListFragment
  }
  ${PremiumFeatureListFragments}
`;

export { FlyerCompositionDetailsFragments };

export default FlyerCompositionDetails;
